import { useMemo, useState } from 'react';
import { Icon } from '../../../components/icons';
import { Card, Field, Input, cn } from '../../../components/ui';
import { CopyButton, ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type Policy = 'none' | 'quarantine' | 'reject';
type Alignment = 'r' | 's';
type Sp = 'inherit' | Policy;
type Fo = '0' | '1' | 'd' | 's';

interface DmarcConfig {
  domain: string;
  policy: Policy;
  pct: number;
  rua: string;
  ruf: string;
  sp: Sp;
  adkim: Alignment;
  aspf: Alignment;
  fo: Fo;
}

const INITIAL: DmarcConfig = {
  domain: '',
  policy: 'none',
  pct: 100,
  rua: '',
  ruf: '',
  sp: 'inherit',
  adkim: 'r',
  aspf: 'r',
  fo: '0',
};

/**
 * Free DMARC TXT-record generator. Pure client-side — assembles the record from
 * the form fields, no backend round-trip needed. Reading the record back is
 * what the /tools/dmarc-checker page does; this page is the inverse, for users
 * who haven't published one yet.
 */
export function DmarcGeneratorPage() {
  const [cfg, setCfg] = useState<DmarcConfig>(INITIAL);

  const record = useMemo(() => buildDmarcRecord(cfg), [cfg]);
  const hostname = cfg.domain ? `_dmarc.${cfg.domain.replace(/^_dmarc\./, '')}` : '_dmarc.yourdomain.com';

  const content: ToolPageContent = {
    slug: 'dmarc-generator',
    title: 'DMARC record generator',
    tagline: 'Build a valid DMARC TXT record in seconds.',
    eyebrow: 'Free tool · No signup · 100% in-browser',
    description:
      'Free DMARC record generator. Build a valid DMARC TXT record with the right policy, pct%, alignment and report addresses — copy it straight to your DNS panel.',
    icon: <Icon.ShieldCheck />,
    accent: 'allow',
    intro:
      'Pick a policy (start with none to monitor), set the aggregate-report address, hit copy — paste the result into your DNS provider as a TXT record under _dmarc.yourdomain.com. No signup, nothing leaves your browser.',
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Configuration</h2>
          <div className="mt-5 grid gap-4">
            <Field label="Your domain">
              <Input
                value={cfg.domain}
                onChange={(e) => setCfg({ ...cfg, domain: e.target.value.toLowerCase().trim() })}
                placeholder="example.com"
                maxLength={253}
                autoFocus
              />
            </Field>

            <Field label="Policy (p=)" hint="Start with none for monitoring; tighten to quarantine then reject when reports look clean.">
              <SegmentedChoice
                value={cfg.policy}
                onChange={(v) => setCfg({ ...cfg, policy: v as Policy })}
                options={[
                  { value: 'none', label: 'none' },
                  { value: 'quarantine', label: 'quarantine' },
                  { value: 'reject', label: 'reject' },
                ]}
              />
            </Field>

            <Field label={`Percentage (pct=${cfg.pct})`} hint="Share of failing mail to apply the policy to. Useful for staged rollouts.">
              <input
                type="range"
                min={1}
                max={100}
                value={cfg.pct}
                onChange={(e) => setCfg({ ...cfg, pct: parseInt(e.target.value, 10) })}
                className="w-full accent-[var(--color-accent)]"
              />
            </Field>

            <Field label="Aggregate reports → (rua=)" hint="Daily summary reports. Pointing somewhere is the whole reason DMARC exists; don't skip this.">
              <Input
                type="email"
                value={cfg.rua}
                onChange={(e) => setCfg({ ...cfg, rua: e.target.value.trim() })}
                placeholder="dmarc-reports@example.com"
                maxLength={254}
              />
            </Field>

            <Field label="Forensic reports → (ruf=)" hint="Per-failure detail reports. Most providers don't honour this; leave blank unless you have a parser.">
              <Input
                type="email"
                value={cfg.ruf}
                onChange={(e) => setCfg({ ...cfg, ruf: e.target.value.trim() })}
                placeholder="(optional)"
                maxLength={254}
              />
            </Field>

            <details className="rounded-md border border-bordersoft bg-panel p-3 text-[13px]">
              <summary className="cursor-pointer font-semibold">Advanced options</summary>
              <div className="mt-4 grid gap-4">
                <Field label="Subdomain policy (sp=)" hint="Defaults to the same as the main policy.">
                  <SegmentedChoice
                    value={cfg.sp}
                    onChange={(v) => setCfg({ ...cfg, sp: v as Sp })}
                    options={[
                      { value: 'inherit', label: 'same' },
                      { value: 'none', label: 'none' },
                      { value: 'quarantine', label: 'quarantine' },
                      { value: 'reject', label: 'reject' },
                    ]}
                  />
                </Field>
                <Field label="DKIM alignment (adkim=)" hint="Relaxed = subdomain ok; strict = exact match required.">
                  <SegmentedChoice
                    value={cfg.adkim}
                    onChange={(v) => setCfg({ ...cfg, adkim: v as Alignment })}
                    options={[
                      { value: 'r', label: 'relaxed' },
                      { value: 's', label: 'strict' },
                    ]}
                  />
                </Field>
                <Field label="SPF alignment (aspf=)" hint="Relaxed = subdomain ok; strict = exact match required.">
                  <SegmentedChoice
                    value={cfg.aspf}
                    onChange={(v) => setCfg({ ...cfg, aspf: v as Alignment })}
                    options={[
                      { value: 'r', label: 'relaxed' },
                      { value: 's', label: 'strict' },
                    ]}
                  />
                </Field>
                <Field label="Failure reporting options (fo=)" hint="0 = both fail; 1 = any fails; d = DKIM fails; s = SPF fails.">
                  <SegmentedChoice
                    value={cfg.fo}
                    onChange={(v) => setCfg({ ...cfg, fo: v as Fo })}
                    options={[
                      { value: '0', label: '0' },
                      { value: '1', label: '1' },
                      { value: 'd', label: 'd' },
                      { value: 's', label: 's' },
                    ]}
                  />
                </Field>
              </div>
            </details>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Your DMARC record</h2>
            <CopyButton text={record} />
          </div>
          <div className="mt-3 font-mono text-[10.5px] uppercase tracking-wider text-faint">Hostname / Name</div>
          <pre className="mt-1 overflow-auto whitespace-pre-wrap break-all rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[12px] text-allow">{hostname}</pre>
          <div className="mt-4 font-mono text-[10.5px] uppercase tracking-wider text-faint">TXT value</div>
          <pre className="mt-1 overflow-auto rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[12px] text-allow whitespace-pre-wrap break-all">
            {record}
          </pre>
          <p className="mt-4 text-[12.5px] text-muted">
            In your DNS provider, add a TXT record with the hostname above and the value above. Allow up to 24h for DNS propagation, then{' '}
            <a href="/tools/dmarc-checker/" className="text-accent hover:underline">verify with our DMARC checker</a>.
          </p>
        </Card>
      </div>
    ),
    educational: {
      title: 'Before you publish what this builds',
      paragraphs: [
        'The generator will happily emit p=reject. Do not publish that first. Start at p=none with a rua address you actually read, spend two to four weeks identifying every legitimate sender on your domain, then ramp.',
        'Going straight to reject is the classic mistake, and it is discovered by way of a marketing CRM nobody remembered that stops delivering.',
      ],
    },
    faq: [
      { q: 'Where do I publish the record?', a: 'As a TXT record on the subdomain _dmarc.yourdomain.com. The value is the full string starting with "v=DMARC1;".' },
      { q: 'What policy should I start with?', a: 'p=none. It collects reports without affecting delivery so you can identify legitimate senders before tightening the policy.' },
      { q: 'Do I need both rua and ruf?', a: 'No. Always set rua (aggregate). ruf (forensic) is rarely useful — most providers don\'t send forensic reports, and the ones that do can leak recipient data.' },
      { q: 'Will this break existing email?', a: 'p=none is safe — it doesn\'t change delivery, only collects reports. Only escalate once your reports show every legitimate sender passing SPF or DKIM alignment.' },
      { q: 'How do I check it\'s working?', a: 'Use our free DMARC checker after publishing. It reads the record at _dmarc.yourdomain.com and confirms the policy + pct are what you expect.' },
    ],
    relatedTools: [
      { slug: 'spf-generator', title: 'SPF record generator', tagline: 'Build a valid SPF record from the senders you authorise.' },
      { slug: 'dkim-generator', title: 'DKIM key generator', tagline: 'Generate an RSA keypair + TXT record in your browser.' },
      { slug: 'dmarc-checker', title: 'DMARC record checker', tagline: 'Verify your published DMARC policy looks right.' },
    ],
    ctaTitle: 'Monitor DMARC on every sending domain',
    ctaBody:
      "SieveGuard's Domain Health add-on tracks SPF, DKIM, DMARC + MX on every domain you ingest, with drift alerts when something changes. Free during your trial.",
  };

  return <ToolPageLayout content={content} />;
}

function buildDmarcRecord(cfg: DmarcConfig): string {
  const parts: string[] = ['v=DMARC1', `p=${cfg.policy}`];
  if (cfg.pct !== 100) parts.push(`pct=${cfg.pct}`);
  if (cfg.sp !== 'inherit') parts.push(`sp=${cfg.sp}`);
  if (cfg.adkim !== 'r') parts.push(`adkim=${cfg.adkim}`);
  if (cfg.aspf !== 'r') parts.push(`aspf=${cfg.aspf}`);
  if (cfg.fo !== '0') parts.push(`fo=${cfg.fo}`);
  if (cfg.rua) parts.push(`rua=mailto:${cfg.rua}`);
  if (cfg.ruf) parts.push(`ruf=mailto:${cfg.ruf}`);
  return parts.join('; ');
}

interface ChoiceOption<T extends string> {
  value: T;
  label: string;
}

function SegmentedChoice<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: ChoiceOption<T>[];
}) {
  return (
    <div className="inline-flex flex-wrap rounded-md border border-border bg-bg p-1 text-[12.5px]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-3 py-1.5 font-mono transition',
            value === o.value ? 'bg-accent text-[#04141d]' : 'text-muted hover:bg-hover hover:text-txt',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Default export required by React Router framework-mode route modules.
export default DmarcGeneratorPage;
